// ======================================================================
// File: VendorQuoteEntry.js
// Improved Vendor Portal UI using existing rfq-ui.css
// ======================================================================

import React, { useEffect, useState } from "react";
import rfqService from "../services/rfqService";
import {
  formatDate,
  formatQuantity
} from "../../../../../../utils/formatUtils";

import "../rfq-ui.css";

const VendorQuoteEntry = () => {

  const [token, setToken] = useState(null);
  const [rfqData, setRfqData] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("token");

    if (!t) {
      setError("Invalid access. Token missing.");
      setLoading(false);
      return;
    }

    setToken(t);
    loadRFQ(t);

  }, []);

  const loadRFQ = async (t) => {
    try {
      await rfqService.validateToken(t);

      const res = await rfqService.getRFQByToken(t);

      setRfqData(res.data.data);

      const initialQuotes = res.data.data.lines.map(line => ({
        rfq_line_id: line.id,
        quoted_price: "",
        tax_amount: "",
        total_amount: "",
        delivery_days: "",
        payment_terms: ""
      }));

      setQuotes(initialQuotes);

    } catch (err) {
      setError(err.response?.data?.message || "Invalid or expired token");
    } finally {
      setLoading(false);
    }
  };

  // ------------------------------------------------------------
  // Auto Calculation Logic
  // ------------------------------------------------------------
  const handleChange = (index, field, value) => {
    const updated = [...quotes];

    updated[index][field] = value;

    const price = parseFloat(updated[index].quoted_price || 0);
    const tax = parseFloat(updated[index].tax_amount || 0);

    updated[index].total_amount = (price + tax).toFixed(2);

    setQuotes(updated);
  };

  const getGrandTotal = () => {
    return quotes.reduce((sum, q) =>
      sum + parseFloat(q.total_amount || 0), 0
    ).toFixed(2);
  };

  const validate = () => {
    for (let q of quotes) {
      if (!q.quoted_price || parseFloat(q.quoted_price) <= 0) {
        setError("Quoted price is mandatory for all items");
        return false;
      }
    }
    return true;
  };

  const submitQuote = async () => {

    if (!validate()) return;

    try {

      const payload = {
        token: token,
        quotes: quotes
      };

      await rfqService.submitQuoteByToken(payload);

      setMessage("Quotation submitted successfully");
      setError("");

    } catch (err) {
      setError(err.response?.data?.message || "Failed to submit quotation");
    }
  };

  if (loading)
    return <div className="rfq-header-card">Loading...</div>;

  if (error)
    return <div className="rfq-header-card">{error}</div>;

  if (message)
    return <div className="rfq-header-card">{message}</div>;

  return (
    <div>

      {/* Header Card */}
      <div className="rfq-header-card">
        <h3>{rfqData.rfq_no}</h3>

<div className="rfq-header-subtitle">
  Quote Due Date :
  {formatDate(rfqData.quote_due_date)}
</div>
      </div>

      {/* Summary Section */}
      <div className="rfq-summary">

        <div className="rfq-summary-card">
          <h4>{rfqData.lines.length}</h4>
          <div>Total Items</div>
        </div>

        <div className="rfq-summary-card">
          <h4>{getGrandTotal()}</h4>
          <div>Grand Total</div>
        </div>

      </div>

      {/* Table Section */}
      <div className="rfq-table-wrapper">

        <table className="rfq-table">
          <thead>
            <tr>
              <th>Material</th>
              <th>Qty</th>
              <th>UOM</th>
              <th>Quoted Price</th>
              <th>Tax Amount</th>
              <th>Total Amount</th>
              <th>Delivery Days</th>
              <th>Payment Terms</th>
            </tr>
          </thead>

          <tbody>
            {rfqData.lines.map((line, index) => (
              <tr key={line.id}>

                <td>{line.material_name}</td>
                <td>{formatQuantity(line.qty)}</td>
                <td>{line.uom}</td>

                <td>
                  <input
                    type="number"
                    value={quotes[index]?.quoted_price}
                    onChange={e =>
                      handleChange(index, "quoted_price", e.target.value)
                    }
                  />
                </td>

                <td>
                  <input
                    type="number"
                    value={quotes[index]?.tax_amount}
                    onChange={e =>
                      handleChange(index, "tax_amount", e.target.value)
                    }
                  />
                </td>

                <td>
                  <input
                    type="text"
                    value={quotes[index]?.total_amount}
                    disabled
                  />
                </td>

                <td>
                  <input
                    type="number"
                    value={quotes[index]?.delivery_days}
                    onChange={e =>
                      handleChange(index, "delivery_days", e.target.value)
                    }
                  />
                </td>

                <td>
                  <input
                    type="text"
                    value={quotes[index]?.payment_terms}
                    onChange={e =>
                      handleChange(index, "payment_terms", e.target.value)
                    }
                  />
                </td>

              </tr>
            ))}
          </tbody>
        </table>

      </div>

      <br />

      <button
        className="btn-primary"
        onClick={submitQuote}
      >
        Submit Quotation
      </button>

    </div>
  );
};

export default VendorQuoteEntry;
